import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { X, Camera, ScanBarcode, Keyboard, Loader2, CheckCircle2, AlertCircle, VideoOff } from 'lucide-react'
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { camerasApi } from '@/api/endpoints'
import { toast } from '@/lib/toast'
import { LookupAddressButton } from '@/components/maps/LookupAddressButton'

interface AddCameraModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Tab = 'manual' | 'scan'

// #040: scanner returns one of two shapes depending on what was scanned.
// FTP cameras: legacy barcode with bare IMEI digits. HTTP cameras: QR with
// JSON payload {Model, IMEI, IMSI, Code}. Auto-detected based on Model field.
type ScanResult =
  | { type: 'ftp'; imei: string }
  | { type: 'http'; imei: string; imsi: string; code: string }

const SCAN_TIMEOUT_MS = 30_000

// localStorage key remembering which camera the user prefers (avoids picking
// virtual cameras like OBS Virtual Camera, MetaMask, etc. that the
// facingMode:environment constraint might match).
const CAMERA_PREF_KEY = 'detercam_scanner_camera_id'

type CameraDevice = { id: string; label: string }

function useHasCamera() {
  const [hasCamera, setHasCamera] = useState<boolean | null>(null)

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setHasCamera(false)
      return
    }
    navigator.mediaDevices.enumerateDevices().then(devices => {
      setHasCamera(devices.some(d => d.kind === 'videoinput'))
    }).catch(() => setHasCamera(false))
  }, [])

  return hasCamera
}

function stopAllTracks() {
  try {
    document.querySelectorAll('video').forEach(video => {
      const stream = video.srcObject as MediaStream | null
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
        video.srcObject = null
      }
    })
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Scanner component — wraps @yudiel/react-qr-scanner, which uses the browser's
// native BarcodeDetector API (Chrome/Edge/Safari) with a Web Worker ZXing
// fallback (Firefox). Replaces the html5-qrcode-based implementation that
// had unreliable QR detection. Format-specific aspect ratio + qrbox geometry
// (square for QR, wide for 1D barcode) is achieved via the toggle below
// and the constraints prop.
// ---------------------------------------------------------------------------

function ScannerView({
  onDetected,
  onTimeout,
  onFallback,
  ingestionType,
  onIngestionTypeChange,
}: {
  onDetected: (result: ScanResult) => void
  onTimeout: () => void
  onFallback: () => void
  ingestionType: 'ftp' | 'http'
  onIngestionTypeChange: (t: 'ftp' | 'http') => void
}) {
  const { t } = useTranslation('cameras')

  const [cameras, setCameras] = useState<CameraDevice[]>([])
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(() => {
    try { return localStorage.getItem(CAMERA_PREF_KEY) } catch { return null }
  })
  const [error, setError] = useState<'denied' | 'generic' | null>(null)
  const detectedRef = useRef(false)

  // Enumerate available cameras for the picker. Labels become real after the
  // user grants camera permission (the Scanner component below will trigger
  // that prompt as soon as it mounts). We re-enumerate on the `devicechange`
  // event so plugging/unplugging a USB webcam updates the dropdown.
  useEffect(() => {
    const refresh = () => {
      if (!navigator.mediaDevices?.enumerateDevices) return
      navigator.mediaDevices.enumerateDevices()
        .then(devs => {
          const list = devs
            .filter(d => d.kind === 'videoinput')
            .map(d => ({ id: d.deviceId, label: d.label || '' }))
          setCameras(list)
          if (selectedCameraId && !list.some(c => c.id === selectedCameraId)) {
            setSelectedCameraId(null)
            try { localStorage.removeItem(CAMERA_PREF_KEY) } catch { /* ignore */ }
          }
        })
        .catch(() => { /* ignore — picker just stays hidden */ })
    }
    refresh()
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh)
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', refresh)
    }
  }, [selectedCameraId])

  // Timeout: if nothing is detected within SCAN_TIMEOUT_MS, fall back to
  // manual entry. Reset on type/camera change because the scanner restarts.
  useEffect(() => {
    detectedRef.current = false
    const timer = setTimeout(() => {
      if (!detectedRef.current) onTimeout()
    }, SCAN_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [ingestionType, selectedCameraId, onTimeout])

  const handleScan = (codes: IDetectedBarcode[]) => {
    if (detectedRef.current || codes.length === 0) return
    const text = codes[0].rawValue

    // #040: try JSON first (HTTP camera QR with {Model, IMEI, IMSI, Code}).
    // The Model="DeterCam" field is the discriminator; if present and
    // IMEI+IMSI parse as digits, treat as HTTP. Otherwise fall through
    // to the legacy digits-only barcode path (FTP cameras).
    try {
      const parsed = JSON.parse(text)
      if (
        parsed &&
        typeof parsed === 'object' &&
        parsed.Model === 'DeterCam' &&
        /^\d{14,17}$/.test(String(parsed.IMEI ?? '')) &&
        /^\d{14,17}$/.test(String(parsed.IMSI ?? ''))
      ) {
        detectedRef.current = true
        onDetected({
          type: 'http',
          imei: String(parsed.IMEI),
          imsi: String(parsed.IMSI),
          code: String(parsed.Code ?? ''),
        })
        return
      }
    } catch { /* not JSON — fall through */ }

    const cleaned = text.replace(/\D/g, '')
    if (cleaned.length < 14 || cleaned.length > 16) return
    detectedRef.current = true
    onDetected({ type: 'ftp', imei: cleaned.slice(0, 15) })
  }

  const handleScanError = (err: unknown) => {
    const msg = String(err)
    if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
      setError('denied')
    } else {
      setError('generic')
    }
  }

  const handleCameraChange = (newId: string) => {
    setSelectedCameraId(newId)
    try { localStorage.setItem(CAMERA_PREF_KEY, newId) } catch { /* ignore */ }
  }

  if (error === 'denied') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 text-center animate-in fade-in duration-300">
        <VideoOff className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t('addCamera.cameraPermissionDenied')}</p>
        <p className="text-xs text-muted-foreground/60">{t('addCamera.tryManualEntry')}</p>
      </div>
    )
  }

  if (error === 'generic') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 text-center animate-in fade-in duration-300">
        <VideoOff className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">{t('addCamera.scannerError')}</p>
        <Button variant="outline" size="sm" className="text-xs" onClick={onFallback}>
          {t('addCamera.tabManual')}
        </Button>
      </div>
    )
  }

  const isQRMode = ingestionType === 'http'
  const containerHeight = isQRMode ? 280 : 200
  // Format-narrowed scan list — passing only the formats we actually expect
  // for the chosen ingestion type lets the decoder skip work it doesn't need.
  const formats = isQRMode
    ? ['qr_code'] as const
    : ['code_128', 'code_39', 'ean_13', 'itf'] as const

  // Constraints: deviceId if user explicitly picked a camera, else
  // facingMode:environment (mobile-friendly: prefers back camera).
  const constraints: MediaTrackConstraints = selectedCameraId
    ? { deviceId: { exact: selectedCameraId } }
    : { facingMode: 'environment' }

  // Scanner is keyed on (ingestionType, selectedCameraId) so React unmounts +
  // remounts the component when either changes — clean teardown of the
  // previous stream + fresh start with the new geometry / device.
  const scannerKey = `${ingestionType}-${selectedCameraId ?? 'auto'}`

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Code-type toggle: square viewfinder + qr_code formats for HTTP camera
          QR codes; wider viewfinder + 1D barcode formats for legacy FTP
          camera barcodes. Changing this re-mounts the scanner with new geometry. */}
      <div className="grid grid-cols-2 gap-1 p-1 bg-muted/40 rounded-md">
        <button
          type="button"
          onClick={() => onIngestionTypeChange('ftp')}
          className={cn(
            'py-1.5 text-[11px] font-medium rounded transition-colors',
            ingestionType === 'ftp'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          FTP barcode
        </button>
        <button
          type="button"
          onClick={() => onIngestionTypeChange('http')}
          className={cn(
            'py-1.5 text-[11px] font-medium rounded transition-colors',
            ingestionType === 'http'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          HTTP QR
        </button>
      </div>

      {/* Camera picker — only when multiple video inputs detected. Useful on
          desktops where OBS Virtual Camera, MetaMask, etc. could otherwise
          be picked by the facingMode constraint. Choice persisted in localStorage. */}
      {cameras.length > 1 && (
        <div>
          <label className="text-[11px] font-medium text-muted-foreground mb-1 block">
            Camera
          </label>
          <select
            value={selectedCameraId ?? ''}
            onChange={e => handleCameraChange(e.target.value)}
            className="w-full h-8 text-xs rounded-md border border-border bg-background px-2"
          >
            {!selectedCameraId && <option value="">Auto (default)</option>}
            {cameras.map(c => (
              <option key={c.id} value={c.id}>{c.label || `Camera ${c.id.slice(0, 6)}`}</option>
            ))}
          </select>
        </div>
      )}

      <div className="relative w-full overflow-hidden rounded-lg bg-black" style={{ height: containerHeight }}>
        <Scanner
          key={scannerKey}
          onScan={handleScan}
          onError={handleScanError}
          formats={formats as unknown as ('qr_code' | 'code_128' | 'code_39' | 'ean_13' | 'itf')[]}
          constraints={constraints}
          scanDelay={150}
          paused={false}
          components={{
            onOff: false,
            torch: false,
            zoom: false,
            finder: true,
          }}
          styles={{
            container: { width: '100%', height: '100%' },
            video: { width: '100%', height: '100%', objectFit: 'cover' },
          }}
        />
      </div>
      <div className="text-center space-y-1">
        <p className="text-xs text-muted-foreground animate-pulse">
          {t('addCamera.pointAtBarcode')}
        </p>
        <p className="text-[10px] text-muted-foreground/50">
          {t('addCamera.scanTimeout')}
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Content height transition
// ---------------------------------------------------------------------------

function ContentTransition({ children, tab }: { children: React.ReactNode; tab: string }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | 'auto'>('auto')

  useEffect(() => {
    if (!contentRef.current) return
    const frame = requestAnimationFrame(() => {
      if (contentRef.current) {
        setHeight(contentRef.current.scrollHeight)
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [tab])

  return (
    <div
      className="overflow-hidden"
      style={{
        height: height === 'auto' ? 'auto' : height,
        transition: 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div ref={contentRef}>
        {children}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export function AddCameraModal({ open, onOpenChange }: AddCameraModalProps) {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const hasCamera = useHasCamera()

  const [tab, setTab] = useState<Tab>('manual')
  const [imei, setImei] = useState('')
  const [name, setName] = useState('')
  const [iccid, setIccid] = useState('')
  const [location, setLocation] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [scanned, setScanned] = useState(false)
  const [error, setError] = useState('')
  // #040: SIM lock-in fields. Populated when scanner detects an HTTP-camera
  // QR; otherwise stay empty for FTP flow. ingestionType also drives which
  // form fields are required (ICCID for FTP, IMSI for HTTP).
  const [ingestionType, setIngestionType] = useState<'ftp' | 'http'>('ftp')
  const [imsi, setImsi] = useState('')
  const [qrCode, setQrCode] = useState('')

  useEffect(() => {
    if (open) {
      setImei('')
      setName('')
      setIccid('')
      setLocation('')
      setLatitude('')
      setLongitude('')
      setScanned(false)
      setError('')
      setTab('manual')
      setIngestionType('ftp')
      setImsi('')
      setQrCode('')
    } else {
      stopAllTracks()
    }
  }, [open])

  useEffect(() => {
    if (tab !== 'scan') stopAllTracks()
  }, [tab])

  const createMutation = useMutation({
    mutationFn: () =>
      camerasApi.create({
        name: name.trim(),
        imei: imei.trim(),
        iccid: iccid.trim() || undefined,
        location: location.trim() || undefined,
        latitude: latitude.trim() || null,
        longitude: longitude.trim() || null,
        // #040: include the SIM lock-in fields. Backend serializer enforces
        // 'imsi required when ingestion_type=http' — we only send these when
        // the user picked HTTP (or scanner auto-detected it).
        ingestion_type: ingestionType,
        ...(ingestionType === 'http' ? { imsi: imsi.trim(), qr_code: qrCode.trim() || undefined } : {}),
      }),
    onSuccess: (camera) => {
      toast.success(t('addCamera.success', { name: camera.name }))
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: ['camera-map-pins'] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const resp = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      const msg = resp
        ? Object.values(resp).flat().join('. ')
        : t('addCamera.genericError')
      // Show as toast (always visible) AND inline below form (if user scrolls).
      // Inline error was hidden below the fold on tall forms.
      setError(msg)
      toast.error(msg)
    },
  })

  const handleBarcodeDetected = (result: ScanResult) => {
    setImei(result.imei)
    if (result.type === 'http') {
      setIngestionType('http')
      setImsi(result.imsi)
      setQrCode(result.code)
    } else {
      setIngestionType('ftp')
      setImsi('')
      setQrCode('')
    }
    setScanned(true)
    setTab('manual')
  }

  const handleScanTimeout = () => {
    setTab('manual')
    setError(t('addCamera.scanTimedOut'))
  }

  // Validation
  const imeiClean = imei.trim().replace(/\D/g, '')
  const imeiValid = imeiClean.length === 15
  const imeiError = imei.trim().length > 0 && !imeiValid

  const iccidClean = iccid.trim().replace(/\D/g, '')
  const iccidValid = iccid.trim() === '' || (iccidClean.length >= 18 && iccidClean.length <= 22)
  const iccidError = iccid.trim().length > 0 && !iccidValid

  // #040: IMSI required for HTTP cameras. 14-17 digits to match backend.
  const imsiClean = imsi.trim().replace(/\D/g, '')
  const imsiValid = ingestionType === 'ftp'
    ? true
    : imsiClean.length >= 14 && imsiClean.length <= 17
  const imsiError = ingestionType === 'http' && imsi.trim().length > 0 && !imsiValid
  const imsiMissing = ingestionType === 'http' && imsi.trim().length === 0

  const latNum = latitude.trim() ? parseFloat(latitude.trim()) : null
  const latValid = latitude.trim() === '' || (latNum !== null && !isNaN(latNum) && latNum >= -90 && latNum <= 90)
  const latError = latitude.trim().length > 0 && !latValid

  const lngNum = longitude.trim() ? parseFloat(longitude.trim()) : null
  const lngValid = longitude.trim() === '' || (lngNum !== null && !isNaN(lngNum) && lngNum >= -180 && lngNum <= 180)
  const lngError = longitude.trim().length > 0 && !lngValid

  const canSubmit =
    imeiValid &&
    name.trim().length > 0 &&
    iccidValid &&
    imsiValid &&
    !imsiMissing &&
    latValid &&
    lngValid &&
    !createMutation.isPending

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{ animationDuration: '200ms' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1"
          style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          <Dialog.Title className="sr-only">{t('addCamera.title')}</Dialog.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Camera className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm font-semibold">{t('addCamera.title')}</p>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Tab toggle */}
          {hasCamera !== false && (
            <div className="flex border-b border-border/60">
              <button
                onClick={() => setTab('manual')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 py-2.5 text-sm transition-all duration-200 border-b-2',
                  tab === 'manual'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <Keyboard className="h-4 w-4" />
                {t('addCamera.tabManual')}
              </button>
              <button
                onClick={() => { setTab('scan'); setError('') }}
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 py-2.5 text-sm transition-all duration-200 border-b-2',
                  tab === 'scan'
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                <ScanBarcode className="h-4 w-4" />
                {t('addCamera.tabScan')}
              </button>
            </div>
          )}

          {/* Content */}
          <ContentTransition tab={tab}>
            {tab === 'scan' ? (
              <div className="px-5 py-4">
                <ScannerView
                  onDetected={handleBarcodeDetected}
                  onTimeout={handleScanTimeout}
                  onFallback={() => setTab('manual')}
                  ingestionType={ingestionType}
                  onIngestionTypeChange={(t) => {
                    setIngestionType(t)
                    // If switching back to FTP, clear any HTTP-only fields
                    // we might have captured from a prior QR scan.
                    if (t === 'ftp') { setImsi(''); setQrCode('') }
                  }}
                />
              </div>
            ) : (
              <div className="px-5 py-4">
                <div className="space-y-3">
                {/* Camera type picker (#040) — pre-selected by scanner via Model:DeterCam discriminator */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Camera type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIngestionType('ftp')
                        setImsi(''); setQrCode('')
                      }}
                      className={cn(
                        'flex items-center justify-center gap-2 py-2 text-xs rounded-md border transition-colors',
                        ingestionType === 'ftp'
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-border text-muted-foreground hover:bg-muted/50'
                      )}
                    >
                      FTP (legacy)
                    </button>
                    <button
                      type="button"
                      onClick={() => setIngestionType('http')}
                      className={cn(
                        'flex items-center justify-center gap-2 py-2 text-xs rounded-md border transition-colors',
                        ingestionType === 'http'
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-border text-muted-foreground hover:bg-muted/50'
                      )}
                    >
                      HTTP (DeterCam)
                    </button>
                  </div>
                </div>

                {/* IMEI */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.imeiLabel')} *</label>
                  <div className="relative">
                    <Input
                      value={imei}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 15)
                        setImei(digits)
                        setError('')
                        setScanned(false)
                      }}
                      placeholder={t('addCamera.imeiPlaceholder')}
                      className={cn('h-9 text-sm pr-8 font-mono', scanned && 'border-emerald-500', imeiError && 'border-destructive')}
                      inputMode="numeric"
                      maxLength={15}
                    />
                    {scanned && imeiValid && (
                      <CheckCircle2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                    )}
                  </div>
                  {scanned && imeiValid && (
                    <p className="text-[11px] text-emerald-600 mt-1">{t('addCamera.scannedSuccess')}</p>
                  )}
                  {imeiError && (
                    <p className="text-[11px] text-destructive mt-1">{t('addCamera.imeiInvalid')}</p>
                  )}
                </div>

                {/* Name */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.nameLabel')} *</label>
                  <Input
                    value={name}
                    onChange={e => { setName(e.target.value); setError('') }}
                    placeholder={t('addCamera.namePlaceholder')}
                    className="h-9 text-sm"
                  />
                </div>

                {/* ICCID — FTP only */}
                {ingestionType === 'ftp' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.iccidLabel')}</label>
                    <Input
                      value={iccid}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 22)
                        setIccid(digits)
                      }}
                      placeholder={t('addCamera.iccidPlaceholder')}
                      className={cn('h-9 text-sm font-mono', iccidError && 'border-destructive')}
                      inputMode="numeric"
                      maxLength={22}
                    />
                    {iccidError && (
                      <p className="text-[11px] text-destructive mt-1">{t('addCamera.iccidInvalid')}</p>
                    )}
                  </div>
                )}

                {/* IMSI — HTTP only, required (#040) */}
                {ingestionType === 'http' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">SIM IMSI *</label>
                    <Input
                      value={imsi}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 17)
                        setImsi(digits)
                      }}
                      placeholder="15-digit IMSI from QR code"
                      className={cn('h-9 text-sm font-mono', imsiError && 'border-destructive')}
                      inputMode="numeric"
                      maxLength={17}
                    />
                    {imsiError && (
                      <p className="text-[11px] text-destructive mt-1">IMSI must be 14–17 digits.</p>
                    )}
                    {imsiMissing && (
                      <p className="text-[11px] text-muted-foreground mt-1">Required for HTTP cameras. Scan the QR code on the camera screen.</p>
                    )}
                  </div>
                )}

                {/* QR Code (Code field) — HTTP only, optional, captured from QR */}
                {ingestionType === 'http' && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">QR pairing code <span className="text-muted-foreground/60">(from QR)</span></label>
                    <Input
                      value={qrCode}
                      onChange={e => setQrCode(e.target.value.slice(0, 20))}
                      placeholder="6-digit code from QR (optional)"
                      className="h-9 text-sm font-mono"
                      maxLength={20}
                    />
                  </div>
                )}

                {/* Location */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.locationLabel')}</label>
                  <Input
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder={t('addCamera.locationPlaceholder')}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Coordinates */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.latitudeLabel')}</label>
                    <Input
                      value={latitude}
                      onChange={e => setLatitude(e.target.value)}
                      placeholder={t('addCamera.latitudePlaceholder')}
                      className={cn('h-9 text-sm font-mono', latError && 'border-destructive')}
                      type="number"
                      step="any"
                      min={-90}
                      max={90}
                    />
                    {latError && (
                      <p className="text-[11px] text-destructive mt-1">{t('addCamera.latitudeInvalid')}</p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{t('addCamera.longitudeLabel')}</label>
                    <Input
                      value={longitude}
                      onChange={e => setLongitude(e.target.value)}
                      placeholder={t('addCamera.longitudePlaceholder')}
                      className={cn('h-9 text-sm font-mono', lngError && 'border-destructive')}
                      type="number"
                      step="any"
                      min={-180}
                      max={180}
                    />
                    {lngError && (
                      <p className="text-[11px] text-destructive mt-1">{t('addCamera.longitudeInvalid')}</p>
                    )}
                  </div>
                </div>
                <LookupAddressButton
                  lat={latitude}
                  lng={longitude}
                  onAddress={(addr) => setLocation(addr)}
                  className="mt-1"
                  buttonLabel={t('edit.lookupAddress')}
                />

                {/* Error */}
                {error && (
                  <p className="flex items-center gap-1.5 text-xs text-destructive animate-in fade-in duration-200">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    {error}
                  </p>
                )}
                </div>
              </div>
            )}
          </ContentTransition>

          {/* Scan footer */}
          {tab === 'scan' && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border/60 bg-muted/20">
              <p className="text-xs text-muted-foreground">{t('addCamera.scanHint')}</p>
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setTab('manual')}>
                {t('addCamera.tabManual')}
              </Button>
            </div>
          )}

          {/* Manual footer */}
          {tab === 'manual' && (
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
              <Dialog.Close asChild>
                <Button variant="ghost" size="sm" className="h-9 text-xs">
                  {tc('actions.cancel')}
                </Button>
              </Dialog.Close>
              <Button
                size="sm"
                className="h-9 text-xs gap-1.5"
                disabled={!canSubmit}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Camera className="h-3.5 w-3.5" />
                }
                {t('addCamera.submit')}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
